import { useState } from 'react';

const propertyTypes = [
  'Detached House',
  'Semi-Detached',
  'Townhouse',
  'Condominium',
  'Other',
];

const timeframes = [
  'Within 1 month',
  '1–3 months',
  '3–6 months',
  '6–12 months',
  'Just exploring options',
];

export default function HomeValuationCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const hp = formData.get('website_alt');
    if (hp && typeof hp === 'string' && hp.trim() !== '') {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }

    formData.delete('website_alt');

    try {
      const res = await fetch(
        'https://readdy.ai/api/form/d9brn59i54tn7t02t4eg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(
            Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
          ).toString(),
        }
      );

      const responseText = await res.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setSubmitted(true);
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message ||
          parsed?.message ||
          'Something went wrong. Please try again.';
        setFormError(
          typeof msg === 'string' && msg.toLowerCase().includes('spam')
            ? 'Your submission could not be processed. Please try again.'
            : msg
        );
      }
    } catch {
      setFormError('Unable to connect. Please check your internet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[600px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-50 mx-auto mb-6">
            <i className="ri-check-line text-primary-500 text-3xl" />
          </div>
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl mb-4">
            Thank You for Your Enquiry
          </h2>
          <p className="text-foreground-600 font-body text-base leading-relaxed mb-6">
            Carol will review your information and get back to you shortly to
            arrange a convenient time to discuss your property. There is no
            obligation — just clear, helpful guidance.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-background-100/70">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="text-primary-500 font-body text-sm tracking-widest uppercase mb-3">
            Free Consultation
          </p>
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-4">
            What Could Your Home Sell For?
          </h2>
          <p className="text-foreground-600 font-body text-base md:text-lg max-w-[650px] mx-auto">
            Property values can change based on location, condition, demand and
            recent local sales. Request a free, no-obligation consultation with
            Carol to gain a clearer understanding of your home's potential
            market value.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          id="home-valuation-cta-form"
          className="max-w-[700px] mx-auto bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                required
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                required
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Telephone Number *
              </label>
              <input
                type="tel"
                name="phone"
                required
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                placeholder="Your phone number"
              />
            </div>
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Property Address *
              </label>
              <input
                type="text"
                name="address"
                required
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                placeholder="Street address"
              />
            </div>
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Property Type
              </label>
              <select
                name="property_type"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              >
                <option value="">Select property type</option>
                {propertyTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Number of Bedrooms
              </label>
              <select
                name="bedrooms"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              >
                <option value="">Select</option>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={String(n)}>
                    {n} {n === 1 ? 'Bedroom' : 'Bedrooms'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Approximate Timeframe
              </label>
              <select
                name="timeframe"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              >
                <option value="">Select timeframe</option>
                {timeframes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
                Preferred Contact Method
              </label>
              <select
                name="contact_method"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              >
                <option value="">Select</option>
                <option value="Phone">Phone</option>
                <option value="Email">Email</option>
                <option value="Either">Either</option>
              </select>
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">
              Message or Additional Information
            </label>
            <textarea
              name="message"
              rows={4}
              maxLength={500}
              className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition resize-none"
              placeholder="Tell Carol about your property and any questions you have..."
            />
          </div>

          {/* Honeypot */}
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-field"
          />

          <div className="mt-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="consent"
                required
                className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
              />
              <span className="text-foreground-600 font-body text-xs leading-relaxed">
                I consent to Carol Berry contacting me about my property
                valuation enquiry. I understand my information will be handled
                in accordance with applicable privacy regulations and will not
                be shared without my permission.
              </span>
            </label>
          </div>

          {formError && (
            <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-md">
              <p className="text-primary-700 font-body text-sm">{formError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all duration-200 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg" />
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-home-4-line text-lg" />
                Request My Free Home Valuation
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}